import { AboutYourLoan, ZERO } from "domain/aboutYourLoan";
import {
  IndicativeRepaymentOptions,
  ExistingLoanDetails,
  RepaymentOption,
  toRepaymentOptionType,
} from "domain/indicativeRepaymentOptions";
import { getFinalRepaymentDate } from "domain/selectedTreatments";
import {
  arrayDecoder,
  Decoder,
  numberDecoder,
  objectDecoder,
  stringDecoder,
} from "json-decoder";
import {
  CalculateIndicativeRepaymentOptionsJson,
  CalculateIndicativeRepaymentOptionsPostJson,
} from "types/types";
import { toDate } from "util/date";

type RepaymentOptionJson =
  CalculateIndicativeRepaymentOptionsJson.Properties.RepaymentOptions.Items;
type ExistingLoanDetailsJson =
  CalculateIndicativeRepaymentOptionsJson.Properties.ExistingLoanDetails;

export const existingLoanDetailsDecoder =
  objectDecoder<ExistingLoanDetailsJson>({
    monthlyRepaymentAmount: numberDecoder,
    totalRepaymentAmount: numberDecoder,
    interestRate: numberDecoder,
    numberOfRepayments: numberDecoder,
    remainingBalance: numberDecoder,
    finalRepaymentDate: stringDecoder,
  });
export const repaymentOptionJsonDecoder = objectDecoder<RepaymentOptionJson>({
  optionType: stringDecoder,
  newMonthlyRepayment: numberDecoder,
  newTotalRepayment: numberDecoder,
  newRemainingTerm: numberDecoder,
  interestDuringHolidayTerm: numberDecoder,
});

export const indicativeRepaymentOptionsJsonDecoder =
  objectDecoder<CalculateIndicativeRepaymentOptionsJson>({
    existingLoanDetails: existingLoanDetailsDecoder,
    repaymentOptions: arrayDecoder(repaymentOptionJsonDecoder),
  });

export const repaymentOptionConverter = (
  json: RepaymentOptionJson
): RepaymentOption => ({
  optionType: toRepaymentOptionType(json.optionType),
  newMonthlyRepayment: json.newMonthlyRepayment,
  newTotalRepayment: json.newTotalRepayment,
  newRemainingTerm: json.newRemainingTerm,
  interestDuringHolidayTerm: json.interestDuringHolidayTerm,
});

export const existingLoanDetailsConverter = (
  json: ExistingLoanDetailsJson
): ExistingLoanDetails => ({
  monthlyRepaymentAmount: json.monthlyRepaymentAmount,
  totalRepaymentAmount: json.totalRepaymentAmount,
  interestRate: json.interestRate,
  numberOfRepayments: json.numberOfRepayments,
  finalRepaymentDate: toDate(json.finalRepaymentDate),
  remainingBalance: json.remainingBalance,
});

export const indicativeRepaymentOptionsConverter = (
  json: CalculateIndicativeRepaymentOptionsJson
): IndicativeRepaymentOptions => ({
  existingLoanDetails: existingLoanDetailsConverter(json.existingLoanDetails),
  repaymentOptions: json.repaymentOptions.map(repaymentOptionConverter),
});

export const indicativeRepaymentOptionsDecoder: Decoder<IndicativeRepaymentOptions> =
  indicativeRepaymentOptionsJsonDecoder.map(
    indicativeRepaymentOptionsConverter
  );

export const indicativeRepaymentOptionsPostJsonEncoder = (
  aboutYourLoan: AboutYourLoan
): CalculateIndicativeRepaymentOptionsPostJson => ({
  finalRepaymentDate: getFinalRepaymentDate(aboutYourLoan.finalRepaymentDate),
  CRHCountTaken: !!aboutYourLoan.CRHTaken ? aboutYourLoan.CRHTaken : ZERO,
  CIRHCountTaken: !!aboutYourLoan.CIRHTaken ? aboutYourLoan.CIRHTaken : ZERO,
  fullTermExtensionTaken: aboutYourLoan.fullTermExtensionTaken,
  remainingBalance: aboutYourLoan.remainingBalance,
  CRHWithTermExtensionCountTaken: !!aboutYourLoan.CRHWithTermExtensionCountTaken
    ? aboutYourLoan.CRHWithTermExtensionCountTaken
    : ZERO,
  CIRHWithTermExtensionCountTaken:
    !!aboutYourLoan.CIRHWithTermExtensionCountTaken
      ? aboutYourLoan.CIRHWithTermExtensionCountTaken
      : ZERO,
});
